import { Checkbox, Stack, Text } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { convertInToCm, modelUtils } from '@pelicargo/utils';
import { DimensionUnit } from '@prisma/client';
import { useMemo } from 'react';

import { TrpcOutputs } from '../../../config/trpc';

type Cargo = TrpcOutputs['getCargosByRequestId'][number];

type Props = {
  cargoList?: Cargo[];
  dimensionUnit?: DimensionUnit;
  /** Cargo ids currently linked to this shipment form. */
  value: number[];
  /** Id of the shipment form this list belongs to. */
  formId: number;
  onChange: (ids: number[]) => void;
  isDisabled?: boolean;
};

/**
 * "Cargo belonging to this shipment" — lets an operator assign cargo pieces to an
 * AWB. A cargo can belong to only one AWB (Cargo.shipment_form_id is single-valued),
 * so cargo already linked to a different form is shown disabled.
 */
export const CargoSelectionList = ({ cargoList, dimensionUnit, value, formId, onChange, isDisabled }: Props) => {
  // useCargos hands out dims in inches; summaryLine expects the stored cm values.
  const summaryLines = useMemo(
    () =>
      new Map(
        cargoList?.map((cargo) => [
          cargo.id,
          modelUtils.cargo.summaryLine(
            {
              ...cargo,
              length: convertInToCm(cargo.length),
              width: convertInToCm(cargo.width),
              height: convertInToCm(cargo.height),
            },
            dimensionUnit,
          ),
        ]),
      ),
    [cargoList, dimensionUnit],
  );

  const toggle = (id: number, checked: boolean) => {
    if (checked) onChange([...new Set([...value, id])]);
    else onChange(value.filter((cargoId) => cargoId !== id));
  };

  return (
    <Stack spacing={1}>
      <Text fontSize="sm" fontWeight="medium" color="fg.muted">
        Cargo belonging to this shipment
      </Text>
      <Show if={!cargoList?.length}>
        <Text fontSize="sm" color="fg.muted">
          No cargo on this request
        </Text>
      </Show>
      {cargoList?.map((cargo) => {
        const isSelected = value.includes(cargo.id);
        const belongsToOther = cargo.shipment_form_id != null && cargo.shipment_form_id !== formId && !isSelected;

        return (
          <Checkbox
            key={cargo.id}
            isChecked={isSelected}
            isDisabled={isDisabled || belongsToOther}
            onChange={(event) => toggle(cargo.id, event.target.checked)}
          >
            <Text fontSize="sm" as="span">
              {summaryLines.get(cargo.id)}
              {belongsToOther ? ' (linked to another AWB)' : ''}
            </Text>
          </Checkbox>
        );
      })}
    </Stack>
  );
};
